import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1600172454132-ada7faa101cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYm90YW5pY2FsJTIwbGVhdmVzJTIwbmF0dXJlJTIwc2hhZG93JTIwc29mdCUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3ODg3NTUxNzN8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 });
  const sy = useSpring(my, { stiffness: 40, damping: 20 });
  const invX = useTransform(sx, (v) => -v);
  const invY = useTransform(sy, (v) => -v);

  const onMouseMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 42);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 42);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pb-24 pt-32"
    >
      <motion.div
        style={{ x: sx, y: sy }}
        className="pointer-events-none absolute -left-32 -top-32 h-[34rem] w-[34rem] rounded-full bg-[#C46B4E]/10 blur-3xl"
      />
      <motion.div
        style={{ x: invX, y: invY }}
        className="pointer-events-none absolute -right-40 top-1/3 h-[38rem] w-[38rem] rounded-full bg-[#7A8B7B]/15 blur-3xl"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8 font-mono text-xs uppercase tracking-[0.24em] text-[#8C827A] sm:text-sm"
            data-testid="hero-eyebrow"
          >
            Integrative Psychotherapy — Online, UK &amp; Beyond
          </motion.p>

          <h1 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#2C2825] sm:text-5xl lg:text-6xl xl:text-7xl">
            <MaskedLine delay={0.35}>
              A safe, <span className="italic text-[#C46B4E]">relational</span> space
            </MaskedLine>
            <MaskedLine delay={0.5}>for healing &amp; inner</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="italic">exploration</span>
              <span className="text-[#C46B4E]">.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8, ease: EASE }}
            className="mt-8 max-w-xl text-lg font-light leading-relaxed text-[#544D46] sm:text-xl"
          >
            I&rsquo;m Simran Kaur, an integrative psychotherapist offering warm, relational
            therapy online &mdash; wherever you are in the UK and beyond.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-book-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#C46B4E] px-7 py-3.5 text-sm font-medium text-[#FAF8F5] transition-colors duration-300 hover:bg-[#B05C41]"
            >
              Book a free 15-minute call
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
            <a
              href="#about"
              data-testid="hero-explore-cta"
              className="group inline-flex items-center gap-2 rounded-full border border-[#2C2825]/15 px-7 py-3.5 text-sm font-medium text-[#2C2825] transition-colors duration-300 hover:border-[#2C2825]/40 hover:bg-white/60"
            >
              Explore the practice
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.35, duration: 0.9 }}
            className="mt-14 flex flex-wrap gap-x-6 gap-y-3 font-mono text-xs uppercase tracking-[0.16em] text-[#8C827A]"
            data-testid="hero-trust-badges"
          >
            <span>6+ years experience</span>
            <span className="text-[#C46B4E]">&bull;</span>
            <span>Relational &amp; psychodynamic</span>
            <span className="text-[#C46B4E]">&bull;</span>
            <span>Online therapy</span>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, duration: 1.2, ease: EASE }}
            className="relative"
          >
            <div className="rotate-2 overflow-hidden rounded-[2rem] border border-[#E3DCD3] shadow-[0_30px_80px_-30px_rgba(44,40,37,0.35)]">
              <img
                src={HERO_IMG}
                alt="Soft botanical shadow cast on a warm plaster wall"
                className="h-[26rem] w-full object-cover sm:h-[30rem]"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.45, duration: 0.8, ease: EASE }}
              className="absolute -bottom-8 -left-4 max-w-xs -rotate-2 rounded-2xl border border-[#E3DCD3] bg-white/85 px-5 py-4 shadow-lg backdrop-blur-md sm:-left-8"
            >
              <p className="font-serif text-lg italic leading-snug text-[#2C2825]">
                &ldquo;Healing begins when we feel safe enough to be fully ourselves.&rdquo;
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
